import { useState, useEffect } from "react";
import LoginPage from "./LoginPage";
import ClaimPage from "./ClaimPage";
import Onboarding from "./Onboarding";
// SetPassword retired from main flow (v4.15) — admin/recovery use only

// ── Geist Font + Global Styles ───────────────────────────────────────────────
const FONT_LINK = document.createElement("link");
FONT_LINK.rel = "stylesheet";
FONT_LINK.href = "https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800&display=swap";
document.head.appendChild(FONT_LINK);

const GLOBAL_STYLE = document.createElement("style");
GLOBAL_STYLE.textContent = `
  *, *::before, *::after { font-family: 'Geist', sans-serif; box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { width: 3px; height: 3px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #1e1e2e; border-radius: 2px; }

  @keyframes fadeUp   { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes breathe  { 0%,100% { text-shadow: 0 0 20px rgba(0,229,160,0.15); } 50% { text-shadow: 0 0 40px rgba(0,229,160,0.4), 0 0 80px rgba(0,229,160,0.15); } }
  @keyframes breatheBox { 0%,100% { box-shadow: 0 0 0 1px #1a1a2a, 0 0 20px rgba(0,229,160,0.04); } 50% { box-shadow: 0 0 0 1px rgba(0,229,160,0.2), 0 0 30px rgba(0,229,160,0.08); } }
  @keyframes pulse    { 0%,100% { opacity:1; } 50% { opacity: 0.4; } }
  @keyframes slideIn  { from { opacity:0; transform: translateX(-6px); } to { opacity:1; transform: translateX(0); } }

  .fade-up    { animation: fadeUp 0.4s ease forwards; }
  .breathe-num { animation: breathe 3s ease-in-out infinite; }
  .breathe-card { animation: breatheBox 4s ease-in-out infinite; }
  .dot-live   { animation: pulse 2.5s ease-in-out infinite; }

  .nav-btn { transition: all 0.25s ease; border-left: 2px solid transparent; }
  .nav-btn:hover { background: rgba(0,229,160,0.05); color: #e0e0f0; border-left-color: rgba(0,229,160,0.3); }
  .nav-btn.active { background: rgba(0,229,160,0.08); color: #00e5a0; border-left-color: #00e5a0; }

  .card { transition: box-shadow 0.3s ease, border-color 0.3s ease; }
  .card:hover { border-color: rgba(0,229,160,0.12) !important; box-shadow: 0 0 24px rgba(0,229,160,0.05); }

  .btn-accent { transition: all 0.2s ease; }
  .btn-accent:hover { background: #00c98d !important; box-shadow: 0 4px 24px rgba(0,229,160,0.3); transform: translateY(-1px); }

  .btn-ghost { transition: all 0.2s ease; }
  .btn-ghost:hover { border-color: rgba(0,229,160,0.3) !important; color: #e0e0f0 !important; }

  .row-hover { transition: background 0.15s ease; }
  .row-hover:hover { background: rgba(255,255,255,0.02); }

  .input-base { transition: border-color 0.2s ease, box-shadow 0.2s ease; }
  .input-base:focus { outline: none; border-color: rgba(0,229,160,0.5) !important; box-shadow: 0 0 0 2px rgba(0,229,160,0.08); }

  .platform-tile { transition: all 0.25s ease; }
  .platform-tile:hover { transform: translateY(-2px); }

  .tab-btn { transition: all 0.2s ease; border-bottom: 2px solid transparent; }
  .tab-btn.active { color: #00e5a0; border-bottom-color: #00e5a0; }
  .tab-btn:hover:not(.active) { color: #c0c0d8; }

  .toggle { transition: background 0.2s ease; cursor: pointer; }
`;
document.head.appendChild(GLOBAL_STYLE);

// ── Design Tokens ────────────────────────────────────────────────────────────
const T = {
  bg:        "#060608",
  surface:   "#0c0c10",
  surface2:  "#101016",
  border:    "#1a1a26",
  border2:   "#232330",
  accent:    "#00e5a0",
  accentDim: "rgba(0,229,160,0.08)",
  accentBorder: "rgba(0,229,160,0.2)",
  text:      "#f0f0fa",
  muted:     "#8888a8",
  dim:       "#4a4a68",
  error:     "#ff6b6b",
  sidebar:   "#08080c",
};

// ── API ──────────────────────────────────────────────────────────────────────
const BASE_URL = "https://melvo-backend-production.up.railway.app";
function getToken() { return localStorage.getItem("melvo_token"); }

async function apiRequest(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

const api = {
  getDashboardStats:  () => apiRequest("/dashboard/stats"),
  getPlatforms:       () => apiRequest("/businesses/me/platforms"),
  getCustomers:       (bId) => apiRequest(`/customers?business_id=${bId}`),
  createCustomer:     (p) => apiRequest("/customers", { method: "POST", body: JSON.stringify(p) }),
  getReviewRequests:  (bId) => apiRequest(`/review-requests?business_id=${bId}`),
  connectPlatform:    (bId, platform, url) => apiRequest("/businesses/me/platforms", { method: "PUT", body: JSON.stringify({ platforms: [{ platform, review_url: url }] }) }),
  disconnectPlatform: (bId, pId) => apiRequest(`/businesses/me/platforms/${pId}`, { method: "DELETE" }),
  getBusiness:        () => apiRequest("/businesses/me"),
  updateBusiness:     (id, p) => apiRequest("/businesses/me", { method: "PUT", body: JSON.stringify(p) }),
  getTemplates:       () => apiRequest("/businesses/me/templates"),
  regenTemplate:      (id) => apiRequest(`/businesses/me/templates/${id}/regenerate`, { method: "POST" }),
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const PLATFORM_META = {
  google:      { color: "#4285F4", logo: "G" },
  yelp:        { color: "#E31B23", logo: "Y" },
  facebook:    { color: "#1877F2", logo: "f" },
  tripadvisor: { color: "#34E0A1", logo: "T" },
  bbb:         { color: "#003B8E", logo: "B" },
  angi:        { color: "#FF6D00", logo: "A" },
};

// ── Sample / Fallback Data ───────────────────────────────────────────────────
const SAMPLE_STATS = {
  totalCustomers: 47,
  requestsSent: 312,
  replyRate: 68,
  recentActivity: [
    { id: 1, type: "review",   text: "Sarah M. clicked your Google review link", created_at: new Date(Date.now() - 1000*60*18).toISOString() },
    { id: 2, type: "request",  text: "James T. was sent a review request via SMS", created_at: new Date(Date.now() - 1000*60*60*1.5).toISOString() },
    { id: 3, type: "customer", text: "New customer added — Maria R.", created_at: new Date(Date.now() - 1000*60*60*3).toISOString() },
    { id: 4, type: "review",   text: "Derek K. left a review on Yelp", created_at: new Date(Date.now() - 1000*60*60*5).toISOString() },
    { id: 5, type: "followup", text: "Follow-up sent to 4 customers with no reply", created_at: new Date(Date.now() - 1000*60*60*22).toISOString() },
    { id: 6, type: "request",  text: "Tanya B. was sent a review request via email", created_at: new Date(Date.now() - 1000*60*60*25).toISOString() },
    { id: 7, type: "review",   text: "Chris W. clicked your Facebook review link", created_at: new Date(Date.now() - 1000*60*60*47).toISOString() },
  ],
};

const SAMPLE_PLATFORMS = [
  { id: "p1", platform: "google",   review_url: "https://g.page/r/example", review_count: 124, avg_rating: 4.8 },
  { id: "p2", platform: "yelp",     review_url: "https://yelp.com/example",  review_count: 38,  avg_rating: 4.6 },
  { id: "p3", platform: "facebook", review_url: "https://fb.com/example",    review_count: 21,  avg_rating: 4.7 },
];

const SAMPLE_MESSAGES = [
  { id: "m1", customer_name: "Sarah M.",  template_preview: "Hey Sarah, thanks so much for coming in today! Would you mind…", platform: "google",   status: "Clicked",   created_at: new Date(Date.now() - 1000*60*18).toISOString() },
  { id: "m2", customer_name: "James T.",  template_preview: "Hi James — we really appreciate your business. A quick review…", platform: "yelp",     status: "Delivered", created_at: new Date(Date.now() - 1000*60*90).toISOString() },
  { id: "m3", customer_name: "Maria R.",  template_preview: "Maria, it was great seeing you today! If you have a moment…", platform: "google",   status: "Sent",      created_at: new Date(Date.now() - 1000*60*60*3).toISOString() },
  { id: "m4", customer_name: "Derek K.",  template_preview: "Thanks for choosing us, Derek. Your feedback means a lot…", platform: "facebook", status: "Clicked",   created_at: new Date(Date.now() - 1000*60*60*5).toISOString() },
  { id: "m5", customer_name: "Tanya B.",  template_preview: "Hi Tanya — hope everything went well. We'd love to hear…", platform: "google",   status: "Delivered", created_at: new Date(Date.now() - 1000*60*60*22).toISOString() },
  { id: "m6", customer_name: "Chris W.",  template_preview: "Chris, thanks for stopping in! A quick Google review would…", platform: "google",   status: "Opted Out", created_at: new Date(Date.now() - 1000*60*60*47).toISOString() },
];

// Map send_jobs status + is_opted_out → display status for Messages page
function normalizeMessageStatus(row) {
  if (row.is_opted_out)            return "Opted Out";
  if (row.status === "delivered")  return "Delivered";
  if (row.status === "held")       return "Sent";
  if (row.status === "delivering") return "Sent";
  if (row.status === "queued")     return "Sent";
  if (row.status === "failed")     return "Failed";
  return row.status || "Sent";
}

function normalizeMessages(rows) {
  return rows.map(r => ({
    ...r,
    status: normalizeMessageStatus(r),
    customer_name: r.customer_name || [r.first_name, r.last_name].filter(Boolean).join(" ") || "Unknown",
  }));
}

const SAMPLE_TEMPLATES = [
  { id: "t1", template_text: "Hey [First Name], thanks for coming in today! We'd love to hear what you thought — a quick review would mean the world to us: [Link] — Reply STOP to opt out", is_active: true,  is_ai_generated: true },
  { id: "t2", template_text: "Hi [First Name]! It was great seeing you. If you have 60 seconds, leaving us a review helps more than you know: [Link] — Reply STOP to opt out", is_active: true,  is_ai_generated: true },
  { id: "t3", template_text: "[First Name], we really appreciate your business. Would you mind sharing your experience? It only takes a minute: [Link] — Reply STOP to opt out", is_active: true,  is_ai_generated: true },
  { id: "t4", template_text: "Thanks for choosing us, [First Name]! Your feedback helps us keep doing what we love. Leave a quick review here: [Link] — Reply STOP to opt out", is_active: false, is_ai_generated: true },
  { id: "t5", template_text: "Hey [First Name] — hope everything went smoothly! If you're happy with your experience, a quick review goes a long way: [Link] — Reply STOP to opt out", is_active: true,  is_ai_generated: true },
  { id: "t6", template_text: "Hi [First Name], it means a lot that you chose [Business Name]. If you have a moment, we'd love a review: [Link] — Reply STOP to opt out", is_active: false, is_ai_generated: true },
];

const SAMPLE_CUSTOMERS = [
  { id: "c1", first_name: "Sarah",  last_name: "Martinez", phone: "(612) 555-0142", email: "sarah.m@email.com",  last_visit_date: new Date(Date.now() - 1000*60*60*2).toISOString(),  status: "active" },
  { id: "c2", first_name: "James",  last_name: "Thompson", phone: "(651) 555-0287", email: null,                  last_visit_date: new Date(Date.now() - 1000*60*60*6).toISOString(),  status: "active" },
  { id: "c3", first_name: "Maria",  last_name: "Rodriguez",phone: "(763) 555-0391", email: "maria.r@email.com",  last_visit_date: new Date(Date.now() - 1000*60*60*14).toISOString(), status: "pending" },
  { id: "c4", first_name: "Derek",  last_name: "Kim",      phone: "(952) 555-0158", email: "derek.k@email.com",  last_visit_date: new Date(Date.now() - 1000*60*60*28).toISOString(), status: "active" },
  { id: "c5", first_name: "Tanya",  last_name: "Brooks",   phone: "(612) 555-0476", email: null,                  last_visit_date: new Date(Date.now() - 1000*60*60*50).toISOString(), status: "active" },
  { id: "c6", first_name: "Chris",  last_name: "Wallace",  phone: "(651) 555-0532", email: "cwallace@email.com",  last_visit_date: new Date(Date.now() - 1000*60*60*72).toISOString(), status: "inactive" },
];

function withSampleFallback(data, sample) {
  if (!data) return sample;
  if (Array.isArray(data) && data.length === 0) return sample;
  if (typeof data === "object" && !Array.isArray(data)) {
    const hasRealData = Object.entries(data).some(([k, v]) => {
      if (k === "recentActivity") return Array.isArray(v) && v.length > 0;
      return typeof v === "number" && v > 0;
    });
    if (!hasRealData) return sample;
  }
  return data;
}

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return "Yesterday";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function activityIcon(type) {
  return { review: "⭐", request: "📨", customer: "👤", platform: "🔗", followup: "🔁" }[type] || "◆";
}

// ── Shared Components ────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
      <span style={{ color: T.dim, fontSize: 13, fontWeight: 300 }}>—</span>
      <span style={{ color: T.muted, fontSize: 12, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase" }}>{children}</span>
    </div>
  );
}

function Skeleton({ w = "100%", h = 12, r = 4 }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: r,
      background: "linear-gradient(90deg, #1a1a26 25%, #22222e 50%, #1a1a26 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.5s infinite",
    }} />
  );
}

function Badge({ children, color = T.accent }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 9px", borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      background: `${color}12`,
      border: `1px solid ${color}30`,
      color: color,
    }}>{children}</span>
  );
}

function ErrorBanner({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ padding: "12px 16px", borderRadius: 10, background: "rgba(255,107,107,0.07)", border: "1px solid rgba(255,107,107,0.2)", color: "#ff9090", fontSize: 13, fontWeight: 300 }}>
      Something went wrong. Try again or email <a href="mailto:hello@melvo.io" style={{ color: "#ff9090", textDecoration: "underline" }}>hello@melvo.io</a>
    </div>
  );
}

// ── Nav Icons ────────────────────────────────────────────────────────────────
const NAV_ICONS = {
  Dashboard: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  Messages: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  Templates: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10,9 9,9 8,9"/>
    </svg>
  ),
  Platforms: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
  ),
  Customers: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Settings: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
};

const NAV_ITEMS = ["Dashboard", "Messages", "Templates", "Platforms", "Settings"];

// ── DASHBOARD PAGE ────────────────────────────────────────────────────────────
function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true); setError(null);
        const [statsData, platformsData] = await Promise.all([api.getDashboardStats(), api.getPlatforms()]);
        if (!cancelled) {
          const rawStats = statsData;
          const livePlatforms = Array.isArray(platformsData?.platforms) ? platformsData.platforms : [];
          setStats(withSampleFallback(rawStats, SAMPLE_STATS));
          setConnectedPlatforms(livePlatforms.length > 0 ? livePlatforms : SAMPLE_PLATFORMS);
        }
      } catch (err) { if (!cancelled) setError(err.message); }
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    const interval = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const statCards = stats ? [
    { label: "Reviews This Month", value: stats.totalCustomers?.toLocaleString() ?? "—", glow: true },
    { label: "Messages Sent", value: stats.requestsSent?.toLocaleString() ?? "—", sub: "of 500 this month" },
    { label: "Avg Response Rate", value: stats.replyRate ? `${stats.replyRate}%` : "—", accent: true },
  ] : [{},{},{}];

  return (
    <div className="fade-up" style={{ display: "flex", flexDirection: "column", gap: 28 }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <p style={{ color: T.dim, fontSize: 11, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
            — Dashboard
          </p>
          <h1 style={{ color: T.text, fontSize: 26, fontWeight: 800, letterSpacing: "-0.03em" }}>
            Overview
          </h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 6 }}>
          <span className="dot-live" style={{ width: 6, height: 6, borderRadius: "50%", background: loading ? T.dim : T.accent, display: "inline-block" }} />
          <span style={{ color: T.dim, fontSize: 12 }}>{loading ? "Syncing…" : "Live"}</span>
        </div>
      </div>

      <ErrorBanner msg={error} />

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
        {loading ? [0,1,2].map(i => (
          <div key={i} className="card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 24 }}>
            <Skeleton w={80} h={40} r={6} />
            <div style={{ marginTop: 10 }}><Skeleton w={120} h={12} /></div>
          </div>
        )) : statCards.map((s, i) => (
          <div key={i} className={`card ${s.glow ? "breathe-card" : ""}`}
            style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 24 }}>
            <div className={s.glow || s.accent ? "breathe-num" : ""}
              style={{
                fontSize: 42, fontWeight: 800, letterSpacing: "-0.04em",
                color: s.accent ? T.accent : T.text,
                lineHeight: 1,
              }}>
              {s.value}
            </div>
            {s.sub && <div style={{ color: T.dim, fontSize: 11, marginTop: 4, fontWeight: 300 }}>{s.sub}</div>}
            <div style={{ color: T.muted, fontSize: 12, fontWeight: 400, marginTop: 8 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Activity + Platforms */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 16 }}>

        {/* Recent Activity */}
        <div className="card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 24 }}>
          <SectionLabel>Recent Activity</SectionLabel>
          {loading ? [0,1,2,3,4].map(i => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: `1px solid ${T.border}` }}>
              <Skeleton w={32} h={32} r={8} />
              <div style={{ flex: 1 }}><Skeleton w="75%" h={12} /></div>
              <Skeleton w={48} h={10} />
            </div>
          )) : (stats?.recentActivity || []).length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: T.dim, fontSize: 13 }}>
              No activity yet — add your first customer to get started.
            </div>
          ) : (stats.recentActivity || []).slice(0, 8).map((a, i) => (
            <div key={a.id || i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 0", borderBottom: `1px solid ${T.border}` }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: T.surface2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 }}>
                {activityIcon(a.type)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ color: "#d0d0e8", fontSize: 13, fontWeight: 300, lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.text}</p>
              </div>
              <span style={{ color: T.dim, fontSize: 11, whiteSpace: "nowrap", flexShrink: 0 }}>
                {a.created_at ? timeAgo(a.created_at) : a.time}
              </span>
            </div>
          ))}
        </div>

        {/* Right column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Top Platforms */}
          <div className="card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 24 }}>
            <SectionLabel>Top Platforms</SectionLabel>
            {connectedPlatforms.length === 0 ? (
              <div style={{ color: T.dim, fontSize: 12, textAlign: "center", padding: "16px 0" }}>No platforms connected yet.</div>
            ) : connectedPlatforms.slice(0, 5).map(p => {
              const meta = PLATFORM_META[p.platform?.toLowerCase()] || { color: "#555", logo: "?" };
              return (
                <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 26, height: 26, borderRadius: 6, background: meta.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: "#fff" }}>{meta.logo}</div>
                    <span style={{ color: "#c0c0d8", fontSize: 13, fontWeight: 400, textTransform: "capitalize" }}>{p.platform}</span>
                  </div>
                  <span style={{ color: T.muted, fontSize: 12, fontWeight: 300 }}>{p.review_count ?? 0} reviews</span>
                </div>
              );
            })}
          </div>

          {/* Quick Actions */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button className="btn-accent" style={{ background: T.accent, color: T.bg, border: "none", borderRadius: 12, padding: "13px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer", letterSpacing: "-0.01em" }}>
              + Add a Customer
            </button>
            <button className="btn-ghost" style={{ background: "transparent", color: T.muted, border: `1px solid ${T.border2}`, borderRadius: 12, padding: "12px 20px", fontSize: 14, fontWeight: 400, cursor: "pointer" }}>
              Send a Message
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MESSAGES PAGE ─────────────────────────────────────────────────────────────
function MessagesPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("All");
  const businessId = localStorage.getItem("melvo_business_id");

  useEffect(() => {
    let cancelled = false;
    api.getReviewRequests(businessId)
      .then(data => { if (!cancelled) setRequests(normalizeMessages(withSampleFallback(Array.isArray(data) ? data : [], SAMPLE_MESSAGES))); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [businessId]);

  const TABS = ["All", "Sent", "Delivered", "Clicked", "Opted Out", "Failed"];
  const filtered = tab === "All" ? requests : requests.filter(r => r.status === tab);

  const statusColor = (s) => ({
    Delivered: "#60a5fa", Clicked: T.accent, "Opted Out": T.error, Sent: T.muted, Failed: T.error,
  })[s] || T.muted;

  return (
    <div className="fade-up" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <p style={{ color: T.dim, fontSize: 11, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>— Messages</p>
          <h1 style={{ color: T.text, fontSize: 26, fontWeight: 800, letterSpacing: "-0.03em" }}>Messages</h1>
        </div>
        <button className="btn-accent" style={{ background: T.accent, color: T.bg, border: "none", borderRadius: 12, padding: "11px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer", marginTop: 4 }}>
          + New Message
        </button>
      </div>

      <ErrorBanner msg={error} />

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.border}` }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} className={`tab-btn ${tab === t ? "active" : ""}`}
            style={{ background: "none", border: "none", borderBottom: "2px solid transparent", padding: "10px 18px", fontSize: 13, fontWeight: tab === t ? 600 : 400, color: tab === t ? T.accent : T.muted, cursor: "pointer" }}>
            {t}
          </button>
        ))}
      </div>

      <div className="card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              {["Customer", "Template", "Platform", "Status", "Sent"].map(h => (
                <th key={h} style={{ padding: "12px 20px", textAlign: "left", color: T.dim, fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? [0,1,2,3].map(i => (
              <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                {[0,1,2,3,4].map(j => <td key={j} style={{ padding: "14px 20px" }}><Skeleton w={j === 0 ? 120 : 80} /></td>)}
              </tr>
            )) : filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: "48px 20px", textAlign: "center", color: T.dim, fontSize: 13, fontWeight: 300 }}>
                No messages yet. Add customers to start sending.
              </td></tr>
            ) : filtered.map(r => (
              <tr key={r.id} className="row-hover" style={{ borderBottom: `1px solid ${T.border}` }}>
                <td style={{ padding: "14px 20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 30, height: 30, borderRadius: "50%", background: T.accentDim, border: `1px solid ${T.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: T.accent, flexShrink: 0 }}>
                      {(r.customer_name || "?")[0].toUpperCase()}
                    </div>
                    <span style={{ color: T.text, fontSize: 13, fontWeight: 400 }}>{r.customer_name}</span>
                  </div>
                </td>
                <td style={{ padding: "14px 20px", maxWidth: 200 }}>
                  <span style={{ color: T.muted, fontSize: 12, fontWeight: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                    {r.template_preview || "—"}
                  </span>
                </td>
                <td style={{ padding: "14px 20px" }}>
                  {r.platform ? (
                    <div style={{ width: 22, height: 22, borderRadius: 5, background: PLATFORM_META[r.platform?.toLowerCase()]?.color || "#333", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#fff" }}>
                      {PLATFORM_META[r.platform?.toLowerCase()]?.logo || "?"}
                    </div>
                  ) : <span style={{ color: T.dim }}>—</span>}
                </td>
                <td style={{ padding: "14px 20px" }}>
                  <Badge color={statusColor(r.status)}>{r.status || "Sent"}</Badge>
                </td>
                <td style={{ padding: "14px 20px", color: T.dim, fontSize: 12, fontWeight: 300 }}>
                  {r.created_at ? timeAgo(r.created_at) : r.date || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── TEMPLATES PAGE ────────────────────────────────────────────────────────────
function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [regening, setRegening] = useState(null);   // template id being regenerated
  const [regenErr, setRegenErr] = useState({});      // { [templateId]: errorString }

  useEffect(() => {
    let cancelled = false;
    api.getTemplates()
      .then(data => { if (!cancelled) setTemplates(withSampleFallback(Array.isArray(data?.templates) ? data.templates : [], SAMPLE_TEMPLATES)); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleToggle = async (t) => {
    setToggling(t.id);
    try {
      await apiRequest(`/businesses/me/templates/${t.id}`, { method: "PUT", body: JSON.stringify({ is_active: !t.is_active }) });
      setTemplates(prev => prev.map(tp => tp.id === t.id ? { ...tp, is_active: !tp.is_active } : tp));
    } catch (err) { alert("Failed to update template."); }
    finally { setToggling(null); }
  };

  const handleRegen = async (t) => {
    setRegening(t.id);
    setRegenErr(prev => ({ ...prev, [t.id]: null }));
    try {
      const data = await api.regenTemplate(t.id);
      setTemplates(prev => prev.map(tp => tp.id === t.id ? { ...tp, template_text: data.template.template_text, regen_count: data.template.regen_count, regen_reset_at: data.template.regen_reset_at } : tp));
    } catch (err) {
      const msg = err.message || "Regeneration failed — please try again.";
      setRegenErr(prev => ({ ...prev, [t.id]: msg }));
    } finally {
      setRegening(null);
    }
  };

  const regenDaysLeft = (t) => {
    if (!t.regen_reset_at) return null;
    const days = Math.ceil((new Date(t.regen_reset_at) - Date.now()) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : null;
  };

  return (
    <div className="fade-up" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <p style={{ color: T.dim, fontSize: 11, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>— Templates</p>
          <h1 style={{ color: T.text, fontSize: 26, fontWeight: 800, letterSpacing: "-0.03em" }}>Your Templates</h1>
          <p style={{ color: T.muted, fontSize: 13, fontWeight: 300, marginTop: 4 }}>
            {loading ? "Loading…" : `20 custom templates — built for you`}
          </p>
        </div>

      </div>

      <ErrorBanner msg={error} />

      {!loading && templates.length === 0 ? (
        <div className="card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 56, textAlign: "center" }}>
          <div style={{ color: T.dim, fontSize: 32, marginBottom: 16 }}>◇</div>
          <p style={{ color: T.muted, fontSize: 14, fontWeight: 300, marginBottom: 20 }}>No templates yet. Complete your setup to generate yours.</p>
          <button className="btn-accent" style={{ background: T.accent, color: T.bg, border: "none", borderRadius: 12, padding: "12px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
            Start Setup →
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}>
          {loading ? [0,1,2,3,4,5].map(i => (
            <div key={i} className="card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20 }}>
              <Skeleton w="90%" h={14} /><div style={{ marginTop: 8 }}><Skeleton w="70%" h={12} /></div>
              <div style={{ marginTop: 16 }}><Skeleton w={60} h={24} r={20} /></div>
            </div>
          )) : templates.map(t => (
            <div key={t.id} className={`card ${t.is_active ? "breathe-card" : ""}`}
              style={{ background: T.surface, border: `1px solid ${t.is_active ? T.accentBorder : T.border}`, borderRadius: 14, padding: 20, transition: "all 0.3s ease" }}>
              <p style={{ color: t.is_active ? "#d8d8f0" : T.muted, fontSize: 13, lineHeight: 1.65, fontWeight: 300, marginBottom: 16, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {t.template_text}
              </p>
              {/* Regen error / limit message */}
              {regenErr[t.id] && (
                <p style={{ color: "rgba(255,107,107,0.8)", fontSize: 11, fontWeight: 300, marginBottom: 10 }}>
                  {regenErr[t.id]}
                </p>
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ color: T.dim, fontSize: 11, fontWeight: 300 }}>{t.template_text.length} chars</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {/* Regenerate button */}
                  {(() => {
                    const daysLeft = regenDaysLeft(t);
                    const limitHit = (t.regen_count || 0) >= 2 && daysLeft;
                    return (
                      <button
                        onClick={() => !limitHit && handleRegen(t)}
                        disabled={regening === t.id || !!limitHit}
                        title={limitHit ? `Regenerations reset in ${daysLeft} day${daysLeft === 1 ? '' : 's'}` : 'Regenerate this template'}
                        style={{
                          background: "transparent",
                          border: `1px solid ${T.border2}`,
                          borderRadius: 20, padding: "4px 12px",
                          color: limitHit ? T.dim : T.muted,
                          fontSize: 11, fontWeight: 500, cursor: limitHit ? "default" : "pointer",
                          opacity: (regening === t.id) ? 0.5 : 1,
                          transition: "all 0.2s",
                        }}>
                        {regening === t.id ? "Regenerating…" : limitHit ? `Reset in ${daysLeft}d` : "↺ Regenerate"}
                      </button>
                    );
                  })()}
                  {/* Active / Inactive toggle */}
                  <button
                    onClick={() => handleToggle(t)}
                    disabled={toggling === t.id}
                    className="toggle"
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      background: t.is_active ? T.accentDim : T.surface2,
                      border: `1px solid ${t.is_active ? T.accentBorder : T.border2}`,
                      borderRadius: 20, padding: "4px 12px 4px 8px",
                      color: t.is_active ? T.accent : T.dim, fontSize: 11, fontWeight: 600,
                      opacity: toggling === t.id ? 0.5 : 1,
                    }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: t.is_active ? T.accent : T.dim }} />
                    {t.is_active ? "Active" : "Inactive"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── PLATFORMS PAGE ────────────────────────────────────────────────────────────
function PlatformsPage() {
  const [platformList, setPlatformList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectingId, setConnectingId] = useState(null);
  const [urlInputs, setUrlInputs] = useState({});
  const businessId = localStorage.getItem("melvo_business_id");

  const ALL_PLATFORMS = [
    { id: "google", name: "Google", ...PLATFORM_META.google },
    { id: "yelp", name: "Yelp", ...PLATFORM_META.yelp },
    { id: "facebook", name: "Facebook", ...PLATFORM_META.facebook },
    { id: "tripadvisor", name: "TripAdvisor", ...PLATFORM_META.tripadvisor },
    { id: "bbb", name: "BBB", ...PLATFORM_META.bbb },
    { id: "angi", name: "Angi", ...PLATFORM_META.angi },
  ];

  useEffect(() => {
    let cancelled = false;
    api.getPlatforms()
      .then(data => { if (!cancelled) setPlatformList(withSampleFallback(Array.isArray(data?.platforms) ? data.platforms : [], SAMPLE_PLATFORMS)); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const connectedIds = new Set(platformList.map(p => p.platform?.toLowerCase()));
  const connectedCount = connectedIds.size;

  const handleConnect = async (platformId) => {
    const url = urlInputs[platformId];
    if (!url?.trim()) return;
    setConnectingId(platformId);
    try {
      const result = await api.connectPlatform(businessId, platformId, url.trim());
      const newPlatforms = Array.isArray(result?.platforms) ? result.platforms : [];
      setPlatformList(prev => [...prev, ...newPlatforms.filter(p => p.platform?.toLowerCase() === platformId)]);
      setUrlInputs(prev => ({ ...prev, [platformId]: "" }));
    } catch (err) { alert("Error: " + err.message); }
    finally { setConnectingId(null); }
  };

  const handleDisconnect = async (platformId) => {
    setConnectingId(platformId);
    try {
      const existing = platformList.find(p => p.platform?.toLowerCase() === platformId);
      await api.disconnectPlatform(businessId, existing.id);
      setPlatformList(prev => prev.filter(p => p.platform?.toLowerCase() !== platformId));
    } catch (err) { alert("Error: " + err.message); }
    finally { setConnectingId(null); }
  };

  return (
    <div className="fade-up" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <p style={{ color: T.dim, fontSize: 11, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>— Platforms</p>
        <h1 style={{ color: T.text, fontSize: 26, fontWeight: 800, letterSpacing: "-0.03em" }}>Review Platforms</h1>
        <p style={{ color: T.muted, fontSize: 13, fontWeight: 300, marginTop: 4 }}>
          Activate the platforms you want reviews on
        </p>
      </div>

      <ErrorBanner msg={error} />

      {/* Coverage bar */}
      <div className="card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ color: T.muted, fontSize: 12, fontWeight: 300 }}>Coverage Score</span>
          <span className="breathe-num" style={{ color: T.accent, fontSize: 13, fontWeight: 700 }}>
            {Math.round((connectedCount / ALL_PLATFORMS.length) * 100)}%
          </span>
        </div>
        <div style={{ height: 3, borderRadius: 2, background: T.border2 }}>
          <div style={{ height: "100%", borderRadius: 2, background: `linear-gradient(90deg, ${T.accent}, #00c98d)`, width: `${(connectedCount / ALL_PLATFORMS.length) * 100}%`, transition: "width 0.5s ease" }} />
        </div>
      </div>

      {/* Platform tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
        {ALL_PLATFORMS.map(p => {
          const connected = connectedIds.has(p.id);
          const platformData = platformList.find(pl => pl.platform?.toLowerCase() === p.id);
          const isBusy = connectingId === p.id;

          return (
            <div key={p.id} className="platform-tile card"
              style={{ background: T.surface, border: `1px solid ${connected ? T.accentBorder : T.border}`, borderRadius: 16, padding: 20, opacity: loading ? 0.6 : 1 }}>

              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: p.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, color: "#fff" }}>
                  {p.logo}
                </div>
                <div style={{
                  fontSize: 11, fontWeight: 500, padding: "3px 10px", borderRadius: 20,
                  background: connected ? T.accentDim : T.surface2,
                  border: `1px solid ${connected ? T.accentBorder : T.border2}`,
                  color: connected ? T.accent : T.dim,
                }}>
                  {connected ? "● Active" : "○ Inactive"}
                </div>
              </div>

              <div style={{ color: T.text, fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{p.name}</div>
              <div style={{ color: T.dim, fontSize: 11, fontWeight: 300, marginBottom: 16 }}>
                {connected && platformData
                  ? `${platformData.review_count ?? 0} reviews connected`
                  : "Simple step-by-step setup — no guesswork"}
              </div>

              {/* URL input when not connected */}
              {!connected && (
                <input
                  className="input-base"
                  placeholder="Paste your review link here"
                  value={urlInputs[p.id] || ""}
                  onChange={e => setUrlInputs(prev => ({ ...prev, [p.id]: e.target.value }))}
                  style={{ width: "100%", background: T.surface2, border: `1px solid ${T.border2}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: T.muted, marginBottom: 12 }}
                />
              )}

              <button
                onClick={() => connected ? handleDisconnect(p.id) : handleConnect(p.id)}
                disabled={isBusy || loading || (!connected && !urlInputs[p.id]?.trim())}
                className={connected ? "btn-ghost" : "btn-accent"}
                style={{
                  width: "100%", borderRadius: 10, padding: "10px",
                  fontSize: 13, fontWeight: 600, cursor: "pointer",
                  background: connected ? "transparent" : T.accent,
                  color: connected ? T.muted : T.bg,
                  border: connected ? `1px solid ${T.border2}` : "none",
                  opacity: (isBusy || (!connected && !urlInputs[p.id]?.trim())) ? 0.5 : 1,
                }}>
                {isBusy ? "…" : connected ? "Disconnect" : "Connect"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── CUSTOMERS PAGE ────────────────────────────────────────────────────────────
function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", last_visit_date: "", consent: false });
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const businessId = localStorage.getItem("melvo_business_id");

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    setImportError(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const res = await fetch("https://melvo-backend-production.up.railway.app/customers/import/csv", {
        method: "POST",
        headers: { "Authorization": `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Import failed.");
      setImportResult(data);
      setImportFile(null);
      // Refresh customer list
      api.getCustomers(businessId)
        .then(d => setCustomers(withSampleFallback(Array.isArray(d) ? d : (d?.customers || []), SAMPLE_CUSTOMERS)))
        .catch(() => {});
    } catch (err) {
      setImportError(err.message);
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.getCustomers(businessId)
      .then(data => { if (!cancelled) setCustomers(withSampleFallback(Array.isArray(data) ? data : (data?.customers || []), SAMPLE_CUSTOMERS)); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [businessId]);

  const filtered = customers.filter(c => {
    const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
    const q = search.toLowerCase();
    return name.includes(q) || c.email?.toLowerCase().includes(q) || c.phone?.includes(q);
  });

  const handleAdd = async () => {
    if (!form.first_name) return alert("First name is required.");
    if (!form.phone && !form.email) return alert("Phone or email is required.");
    if (!form.consent) return alert("Customer consent is required before sending texts.");
    try {
      setSaving(true);
      const payload = { first_name: form.first_name.trim(), last_name: form.last_name.trim() || null, phone: form.phone.trim() || null, email: form.email.trim() || null, last_visit_date: form.last_visit_date || null, business_id: businessId };
      const result = await api.createCustomer(payload);
      setCustomers([result?.customer || result, ...customers]);
      setForm({ first_name: "", last_name: "", phone: "", email: "", last_visit_date: "", consent: false });
      setShowForm(false);
    } catch (err) { alert("Failed to save: " + err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fade-up" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <p style={{ color: T.dim, fontSize: 11, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>— Customers</p>
          <h1 style={{ color: T.text, fontSize: 26, fontWeight: 800, letterSpacing: "-0.03em" }}>Customers</h1>
          <p style={{ color: T.muted, fontSize: 13, fontWeight: 300, marginTop: 4 }}>
            {loading ? "Loading…" : `${customers.length} total`}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <button onClick={() => { setShowImport(!showImport); setShowForm(false); }} className="btn-ghost"
            style={{ background: "transparent", color: T.muted, border: `1px solid ${T.border2}`, borderRadius: 12, padding: "11px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            ↑ Import CSV
          </button>
          <button onClick={() => { setShowForm(!showForm); setShowImport(false); }} className="btn-accent"
            style={{ background: T.accent, color: T.bg, border: "none", borderRadius: 12, padding: "11px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            + Add Customer
          </button>
        </div>
      </div>

      <ErrorBanner msg={error} />

      {showImport && (
        <div className="card fade-up" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 24 }}>
          <SectionLabel>Import Customers from CSV</SectionLabel>
          <p style={{ color: T.muted, fontSize: 12, fontWeight: 300, marginBottom: 16, lineHeight: 1.7 }}>
            Upload a CSV file exported from your POS system. First row must be column headers. Accepted columns include: first_name, last_name, phone, email, and date of service variants. Max 50MB.
          </p>

          {!importResult ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div
                style={{ border: `2px dashed ${importFile ? T.accentBorder : T.border2}`, borderRadius: 12, padding: "32px 24px", textAlign: "center", cursor: "pointer", transition: "border-color 0.2s", background: importFile ? T.accentDim : "transparent" }}
                onClick={() => document.getElementById("csv-file-input").click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.name.endsWith(".csv")) setImportFile(f); else alert("Please upload a .csv file."); }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
                <div style={{ color: importFile ? T.accent : T.muted, fontSize: 13, fontWeight: importFile ? 600 : 300 }}>
                  {importFile ? importFile.name : "Click to choose a file or drag and drop"}
                </div>
                {importFile && (
                  <div style={{ color: T.dim, fontSize: 11, marginTop: 4 }}>
                    {(importFile.size / 1024).toFixed(1)} KB
                  </div>
                )}
                <input id="csv-file-input" type="file" accept=".csv" style={{ display: "none" }}
                  onChange={e => { const f = e.target.files[0]; if (f) setImportFile(f); }} />
              </div>

              {importError && <p style={{ color: "#ff6b6b", fontSize: 13, fontWeight: 400 }}>{importError}</p>}

              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={handleImport} disabled={!importFile || importing} className="btn-accent"
                  style={{ background: importFile && !importing ? T.accent : T.surface2, color: importFile && !importing ? T.bg : T.dim, border: "none", borderRadius: 10, padding: "11px 20px", fontSize: 13, fontWeight: 700, cursor: importFile && !importing ? "pointer" : "not-allowed" }}>
                  {importing ? "Importing…" : "Import"}
                </button>
                <button onClick={() => { setShowImport(false); setImportFile(null); setImportError(null); }} className="btn-ghost"
                  style={{ background: "transparent", color: T.muted, border: `1px solid ${T.border2}`, borderRadius: 10, padding: "11px 20px", fontSize: 13, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
                {[
                  { label: "In File",       value: importResult.total_in_file,    accent: false },
                  { label: "Imported",      value: importResult.imported,          accent: true  },
                  { label: "Opted Out",     value: importResult.opt_out_skipped,   accent: false },
                  { label: "Failed",        value: importResult.failed,            accent: importResult.failed > 0 ? "error" : false },
                ].map(({ label, value, accent }) => (
                  <div key={label} style={{ background: T.surface2, border: `1px solid ${T.border2}`, borderRadius: 12, padding: "16px 20px", textAlign: "center" }}>
                    <div style={{ color: accent === "error" ? "#ff6b6b" : accent ? T.accent : T.text, fontSize: 22, fontWeight: 800, letterSpacing: "-0.03em" }}>{value ?? "—"}</div>
                    <div style={{ color: T.dim, fontSize: 10, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 4 }}>{label}</div>
                  </div>
                ))}
              </div>

              {importResult.failed > 0 && importResult.failed_rows?.length > 0 && (
                <details style={{ background: T.surface2, border: "1px solid rgba(255,107,107,0.2)", borderRadius: 10, padding: "12px 16px" }}>
                  <summary style={{ color: "#ff6b6b", fontSize: 12, fontWeight: 600, cursor: "pointer", letterSpacing: "0.02em" }}>
                    {importResult.failed} row{importResult.failed !== 1 ? "s" : ""} failed — view details
                  </summary>
                  <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                    {importResult.failed_rows.map(({ row, reason }) => (
                      <div key={row} style={{ display: "flex", gap: 12, fontSize: 12 }}>
                        <span style={{ color: T.dim, fontWeight: 500, minWidth: 48 }}>Row {row}</span>
                        <span style={{ color: T.muted, fontWeight: 300 }}>{reason}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              <p style={{ color: T.accent, fontSize: 13, fontWeight: 500 }}>✓ Import complete.</p>
              <button onClick={() => { setImportResult(null); setShowImport(false); }} className="btn-ghost"
                style={{ background: "transparent", color: T.muted, border: `1px solid ${T.border2}`, borderRadius: 10, padding: "11px 20px", fontSize: 13, cursor: "pointer", alignSelf: "flex-start" }}>
                Done
              </button>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="card fade-up" style={{ background: T.surface, border: `1px solid ${T.accentBorder}`, borderRadius: 16, padding: 24 }}>
          <SectionLabel>New Customer</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            {[
              { key: "first_name", placeholder: "First Name *" },
              { key: "last_name",  placeholder: "Last Name" },
              { key: "phone",      placeholder: "Phone Number *" },
              { key: "email",      placeholder: "Email Address" },
            ].map(f => (
              <input key={f.key} className="input-base" placeholder={f.placeholder} value={form[f.key]}
                onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                style={{ background: T.surface2, border: `1px solid ${T.border2}`, borderRadius: 10, padding: "11px 14px", fontSize: 13, color: T.text }} />
            ))}
            <div style={{ gridColumn: "span 2", display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ color: T.dim, fontSize: 11 }}>Date of Service</label>
              <input type="date" className="input-base" value={form.last_visit_date}
                onChange={e => setForm({ ...form, last_visit_date: e.target.value })}
                style={{ background: T.surface2, border: `1px solid ${T.border2}`, borderRadius: 10, padding: "11px 14px", fontSize: 13, color: T.text, colorScheme: "dark" }} />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: 14, borderRadius: 10, background: T.surface2, border: `1px solid ${T.border2}`, marginBottom: 16 }}>
            <input type="checkbox" id="consent" checked={form.consent} onChange={e => setForm({ ...form, consent: e.target.checked })}
              style={{ width: 16, height: 16, marginTop: 2, accentColor: T.accent, flexShrink: 0, cursor: "pointer" }} />
            <label htmlFor="consent" style={{ color: T.muted, fontSize: 12, lineHeight: 1.6, fontWeight: 300, cursor: "pointer" }}>
              Customer verbally agreed to receive a follow-up text from this business. <span style={{ color: T.dim }}>(Required for TCPA compliance)</span>
            </label>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={handleAdd} disabled={saving || !form.consent} className="btn-accent"
              style={{ background: form.consent ? T.accent : T.surface2, color: form.consent ? T.bg : T.dim, border: "none", borderRadius: 10, padding: "11px 20px", fontSize: 13, fontWeight: 700, cursor: form.consent ? "pointer" : "not-allowed", opacity: saving ? 0.6 : 1 }}>
              {saving ? "Saving…" : "Save Customer"}
            </button>
            <button onClick={() => setShowForm(false)} className="btn-ghost"
              style={{ background: "transparent", color: T.muted, border: `1px solid ${T.border2}`, borderRadius: 10, padding: "11px 20px", fontSize: 13, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}` }}>
          <input className="input-base" placeholder="Search customers…" value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ background: T.surface2, border: `1px solid ${T.border2}`, borderRadius: 8, padding: "8px 14px", fontSize: 13, color: T.text, width: 260 }} />
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              {["Name", "Phone", "Email", "Date of Service", "Status"].map(h => (
                <th key={h} style={{ padding: "12px 20px", textAlign: "left", color: T.dim, fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? [0,1,2,3].map(i => (
              <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                {[0,1,2,3,4].map(j => <td key={j} style={{ padding: "14px 20px" }}><Skeleton w={j === 0 ? 120 : 80} /></td>)}
              </tr>
            )) : filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: "48px 20px", textAlign: "center", color: T.dim, fontSize: 13, fontWeight: 300 }}>
                {search ? "No customers match your search." : "No customers yet — add your first one above."}
              </td></tr>
            ) : filtered.map(c => {
              const fullName = `${c.first_name || ""} ${c.last_name || ""}`.trim();
              return (
                <tr key={c.id} className="row-hover" style={{ borderBottom: `1px solid ${T.border}` }}>
                  <td style={{ padding: "14px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: T.accentDim, border: `1px solid ${T.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: T.accent, flexShrink: 0 }}>
                        {(c.first_name || "?")[0].toUpperCase()}
                      </div>
                      <span style={{ color: T.text, fontSize: 13, fontWeight: 400 }}>{fullName}</span>
                    </div>
                  </td>
                  <td style={{ padding: "14px 20px", color: T.muted, fontSize: 13, fontWeight: 300 }}>{c.phone || "—"}</td>
                  <td style={{ padding: "14px 20px", color: T.muted, fontSize: 13, fontWeight: 300 }}>{c.email || "—"}</td>
                  <td style={{ padding: "14px 20px", color: T.muted, fontSize: 13, fontWeight: 300 }}>
                    {c.last_visit_date ? (() => { const [y,m,d] = c.last_visit_date.split("T")[0].split("-"); return new Date(y,m-1,d).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}); })() : "—"}
                  </td>
                  <td style={{ padding: "14px 20px" }}>
                    <Badge color={c.status === "active" ? T.accent : T.dim}>
                      {c.status === "pending" ? "Pending" : c.status === "active" ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── SETTINGS PAGE ─────────────────────────────────────────────────────────────
function SettingsPage({ msgCap }) {
  const [settings, setSettings] = useState({
    businessName: "", requestDelay: "2", followUpDelay: "4",
    preferredSendHour: "18",
    smsTemplate: "Hi {first_name}, thanks for visiting {business_name}! We'd love your review: {review_url} — Reply STOP to opt out",
    emailSubject: "How was your visit, {first_name}?",
    emailTemplate: "Hi {first_name},\n\nThank you for choosing {business_name}! Would you mind leaving us a quick review?\n\n{review_url}\n\nThanks,\n{business_name} Team",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = (k, v) => setSettings(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    api.getBusiness()
      .then(data => {
        if (!cancelled && data?.business) {
          const b = data.business;
          setSettings(prev => ({ ...prev, businessName: b.name || "", requestDelay: b.sms_delay_hours?.toString() || "2", followUpDelay: b.email_delay_days?.toString() || "4", preferredSendHour: b.preferred_send_hour != null ? b.preferred_send_hour.toString() : "18", smsTemplate: b.sms_template || prev.smsTemplate, emailSubject: b.email_subject || prev.emailSubject, emailTemplate: b.email_template || prev.emailTemplate }));
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.updateBusiness(null, { name: settings.businessName, sms_delay_hours: parseInt(settings.requestDelay), email_delay_days: parseInt(settings.followUpDelay), preferred_send_hour: settings.preferredSendHour !== "" ? parseInt(settings.preferredSendHour) : null, sms_template: settings.smsTemplate, email_subject: settings.emailSubject, email_template: settings.emailTemplate });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) { alert("Failed to save: " + err.message); }
    finally { setSaving(false); }
  };

  const inputStyle = { background: T.surface2, border: `1px solid ${T.border2}`, borderRadius: 10, padding: "11px 14px", fontSize: 13, color: T.text, width: "100%" };
  const labelStyle = { color: T.dim, fontSize: 11, fontWeight: 400, display: "block", marginBottom: 6 };

  return (
    <div className="fade-up" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <p style={{ color: T.dim, fontSize: 11, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>— Settings</p>
        <h1 style={{ color: T.text, fontSize: 26, fontWeight: 800, letterSpacing: "-0.03em" }}>Settings</h1>
        <p style={{ color: T.muted, fontSize: 13, fontWeight: 300, marginTop: 4 }}>Configure your review automation</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

        {/* Business Info */}
        <div className="card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 24 }}>
          <SectionLabel>Business Info</SectionLabel>
          <label style={labelStyle}>Business Name</label>
          <input className="input-base" value={settings.businessName} onChange={e => set("businessName", e.target.value)} placeholder={loading ? "Loading…" : "Your Business Name"} style={inputStyle} />
        </div>

        {/* Timing */}
        <div className="card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 24 }}>
          <SectionLabel>Request Timing</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={labelStyle}>Send first request after visit (hours)</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <input type="number" className="input-base" value={settings.requestDelay} onChange={e => set("requestDelay", e.target.value)}
                  style={{ ...inputStyle, width: 80, textAlign: "center", color: T.accent }} />
                <span style={{ color: T.dim, fontSize: 12, fontWeight: 300 }}>hours after visit (recommended: 2)</span>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Send follow-up if no reply (days)</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <input type="number" className="input-base" value={settings.followUpDelay} onChange={e => set("followUpDelay", e.target.value)}
                  style={{ ...inputStyle, width: 80, textAlign: "center", color: T.accent }} />
                <span style={{ color: T.dim, fontSize: 12, fontWeight: 300 }}>days (recommended: 4)</span>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Preferred send time</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <select
                  className="input-base"
                  value={settings.preferredSendHour}
                  onChange={e => set("preferredSendHour", e.target.value)}
                  style={{ ...inputStyle, width: "auto", color: T.accent, cursor: "pointer" }}
                >
                  {[
                    { value: "8",  label: "8:00 AM" },
                    { value: "9",  label: "9:00 AM" },
                    { value: "10", label: "10:00 AM" },
                    { value: "11", label: "11:00 AM" },
                    { value: "12", label: "12:00 PM (noon)" },
                    { value: "13", label: "1:00 PM" },
                    { value: "14", label: "2:00 PM" },
                    { value: "15", label: "3:00 PM" },
                    { value: "16", label: "4:00 PM" },
                    { value: "17", label: "5:00 PM" },
                    { value: "18", label: "6:00 PM (recommended)" },
                    { value: "19", label: "7:00 PM" },
                    { value: "20", label: "8:00 PM" },
                  ].map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <span style={{ color: T.dim, fontSize: 12, fontWeight: 300 }}>business local time</span>
              </div>
              <p style={{ color: T.dim, fontSize: 11, marginTop: 6, fontWeight: 300 }}>TCPA window (8am–9pm) always enforced.</p>
            </div>
          </div>
        </div>

        {/* SMS Template */}
        <div className="card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <SectionLabel>SMS Template</SectionLabel>
            <div style={{ display: "flex", gap: 6 }}>
              {["{first_name}", "{review_url}"].map(t => (
                <span key={t} style={{ background: T.accentDim, color: T.accent, fontSize: 10, padding: "2px 7px", borderRadius: 4, fontWeight: 500 }}>{t}</span>
              ))}
            </div>
          </div>
          <textarea className="input-base" value={settings.smsTemplate} onChange={e => set("smsTemplate", e.target.value)}
            rows={4} style={{ ...inputStyle, resize: "none", lineHeight: 1.7, color: "#c8c8e0" }} />
          <div style={{ color: T.dim, fontSize: 11, marginTop: 6, fontWeight: 300 }}>{settings.smsTemplate.length} / 160 characters</div>
        </div>

        {/* Email Template */}
        <div className="card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 24 }}>
          <SectionLabel>Email Template</SectionLabel>
          <label style={labelStyle}>Subject Line</label>
          <input className="input-base" value={settings.emailSubject} onChange={e => set("emailSubject", e.target.value)} style={{ ...inputStyle, marginBottom: 12 }} />
          <textarea className="input-base" value={settings.emailTemplate} onChange={e => set("emailTemplate", e.target.value)}
            rows={6} style={{ ...inputStyle, resize: "none", lineHeight: 1.7, color: "#c8c8e0" }} />
        </div>

        {/* Billing */}
        <div className="card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 24 }}>
          <SectionLabel>Billing</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: T.muted, fontSize: 13, fontWeight: 300 }}>Current Plan</span>
              <Badge>Pro — $59/mo</Badge>
            </div>
            <div style={{ height: 1, background: T.border }} />

            {/* Message usage breakdown */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: T.muted, fontSize: 13, fontWeight: 300 }}>Monthly messages</span>
                <span style={{ color: T.text, fontSize: 13, fontWeight: 400 }}>
                  {msgCap.used} used / {msgCap.cap} included
                </span>
              </div>
              {msgCap.addon > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: T.muted, fontSize: 13, fontWeight: 300 }}>Banked messages</span>
                  <span style={{ color: T.accent, fontSize: 13, fontWeight: 500 }}>
                    {msgCap.addon} remaining
                  </span>
                </div>
              )}
            </div>

            <div style={{ height: 1, background: T.border }} />
            <button
              className="btn-ghost"
              style={{ background: "transparent", color: T.muted, border: `1px solid ${T.border2}`, borderRadius: 10, padding: "10px 16px", fontSize: 13, cursor: "pointer", textAlign: "left" }}
              onClick={async () => {
                try {
                  const res = await fetch("https://melvo-backend-production.up.railway.app/billing/portal", {
                    method: "POST",
                    headers: { "Authorization": `Bearer ${getToken()}`, "Content-Type": "application/json" }
                  });
                  const data = await res.json();
                  if (data?.url) window.location.href = data.url;
                } catch (e) { alert("Could not open billing portal. Please try again."); }
              }}>
              Manage Billing →
            </button>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="card" style={{ background: T.surface, border: "1px solid rgba(255,107,107,0.15)", borderRadius: 16, padding: 24 }}>
          <SectionLabel>Danger Zone</SectionLabel>
          <button
            style={{ background: "transparent", color: "rgba(255,107,107,0.6)", border: "1px solid rgba(255,107,107,0.2)", borderRadius: 10, padding: "10px 16px", fontSize: 12, fontWeight: 400, cursor: "pointer" }}
            onClick={async () => {
              if (!window.confirm("Cancel your Melvo subscription? Your account stays active until the end of the billing period.")) return;
              try {
                const res = await fetch("https://melvo-backend-production.up.railway.app/billing/cancel", {
                  method: "POST",
                  headers: { "Authorization": `Bearer ${getToken()}`, "Content-Type": "application/json" }
                });
                const data = await res.json();
                if (res.ok) alert("Subscription cancelled. You'll have access until the end of your billing period.");
                else alert(data?.error || "Could not cancel. Please contact hello@melvo.io.");
              } catch (e) { alert("Could not cancel. Please contact hello@melvo.io."); }
            }}>
            Cancel Subscription
          </button>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12 }}>
        {saved && <span style={{ color: T.accent, fontSize: 13, fontWeight: 500 }}>Done. Changes saved.</span>}
        <button onClick={handleSave} disabled={saving} className="btn-accent"
          style={{ background: T.accent, color: T.bg, border: "none", borderRadius: 12, padding: "12px 28px", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

// ── APP SHELL ─────────────────────────────────────────────────────────────────
export default function MelvoDashboard() {
  const [page, setPage]                     = useState("Dashboard");
  const [authed, setAuthed]                 = useState(!!localStorage.getItem("melvo_token"));
  const [businessName, setBusinessName]     = useState("");
  const [msgCap, setMsgCap]                 = useState({ used: 0, cap: 500, addon: 0 });
  const [onboardingDone, setOnboardingDone] = useState(true);

  // ── Google OAuth callback handler ───────────────────────────────────────
  // Supabase redirects back to / with #access_token=...&token_type=bearer
  // If melvo_claim_token is in sessionStorage, send it with exchange (claim flow).
  // Otherwise standard returning-customer login.
  useEffect(() => {
    const hash = window.location.hash;
    const isGoogleCallback =
      hash.includes("access_token") &&
      hash.includes("token_type=bearer") &&
      !hash.includes("type=invite") &&
      !hash.includes("type=recovery");

    if (!isGoogleCallback) return;

    window.history.replaceState(null, "", window.location.pathname);

    const params      = new URLSearchParams(hash.replace(/^#/, ""));
    const accessToken = params.get("access_token");
    if (!accessToken) return;

    (async () => {
      try {
        const claimToken = sessionStorage.getItem("melvo_claim_token");
        const body = claimToken
          ? { access_token: accessToken, claim_token: claimToken }
          : { access_token: accessToken };

        const res  = await fetch(`${BASE_URL}/auth/magic-exchange`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Exchange failed");

        localStorage.setItem("melvo_token",       data.token);
        localStorage.setItem("melvo_business_id", data.business_id || "");
        if (claimToken) sessionStorage.removeItem("melvo_claim_token");
        setAuthed(true);
      } catch (err) {
        console.error("[Auth] Google OAuth exchange failed:", err.message);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch business data when authed ─────────────────────────────────────
  // Must be declared before any conditional returns (Rules of Hooks).
  useEffect(() => {
    if (authed) {
      api.getBusiness()
        .then(data => {
          if (data?.business?.name) setBusinessName(data.business.name);
          if (data?.business?.onboarding_complete === false) setOnboardingDone(false);
          if (data?.business) {
            setMsgCap({
              used:  data.business.messages_used   || 0,
              cap:   data.business.message_cap     || 500,
              addon: data.business.addon_balance   || 0,
            });
          }
        })
        .catch(() => {});
    }
  }, [authed]);

  // ── /addon-success — post add-on purchase confirmation ─────────────────
  // No auth required. Stripe add-on payment link redirects here.
  if (window.location.pathname === "/addon-success") {
    return (
      <div style={{ background: "#060608", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: "100%", maxWidth: 480, padding: "0 24px", textAlign: "center" }}>
          <a href="https://melvo.io" target="_blank" rel="noreferrer" style={{ display: "inline-block", lineHeight: 0, marginBottom: 40 }}>
            <img src="/melvo-logo.png" alt="Melvo" style={{ height: 100 }} />
          </a>
          <div style={{ background: "#0c0c10", border: "1px solid #1a1a2e", borderRadius: 20, padding: "48px 40px" }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(0,229,160,0.12)", border: "1px solid rgba(0,229,160,0.25)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, margin: "0 auto 20px" }}>✓</div>
            <h1 style={{ color: "#f0f0f8", fontSize: 22, fontWeight: 800, letterSpacing: "-0.03em", margin: "0 0 16px" }}>
              Payment received
            </h1>
            <p style={{ color: "#a0a0c0", fontSize: 15, lineHeight: 1.7, margin: "0 0 10px" }}>
              250 messages have been added to your account.
            </p>
            <p style={{ color: "#6a6a84", fontSize: 14, lineHeight: 1.7, margin: "0 0 36px" }}>
              They'll carry over each month until you use them.
            </p>
            <a
              href="https://app.melvo.io"
              style={{ display: "inline-block", background: "#00e5a0", color: "#060608", fontWeight: 700, fontSize: 15, padding: "14px 32px", borderRadius: 12, textDecoration: "none" }}
            >
              View Account →
            </a>
          </div>
          <p style={{ color: "#4a4a5e", fontSize: 12, marginTop: 24 }}>© 2026 Melvo. All rights reserved.</p>
        </div>
      </div>
    );
  }

  // /check-email and /set-password routes retired in v4.15.
  // Stripe now redirects to /login. Auth = Google OAuth or Phone OTP.

  // ── /claim — new customer arriving from welcome email ───────────────────
  if (window.location.pathname === "/claim") {
    return <ClaimPage onClaimed={() => setAuthed(true)} />;
  }

  if (!authed) return <LoginPage onLogin={() => setAuthed(true)} />;

  if (!onboardingDone) return (
    <Onboarding
      token={localStorage.getItem("melvo_token")}
      onComplete={() => setOnboardingDone(true)}
    />
  );

  const initials = businessName
    ? businessName.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)
    : "M";

  const pct = Math.min(100, Math.round((msgCap.used / msgCap.cap) * 100));
  const arcColor = pct >= 80 ? "#f59e0b" : pct >= 95 ? T.error : T.accent;

  const pages = {
    Dashboard: <DashboardPage />,
    Messages:  <MessagesPage />,
    Templates: <TemplatesPage />,
    Platforms: <PlatformsPage />,
    Customers: <CustomersPage />,
    Settings:  <SettingsPage msgCap={msgCap} />,
  };

  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden", background: T.bg, color: T.text }}>

      {/* ── Sidebar ── */}
      <aside style={{ width: 240, flexShrink: 0, display: "flex", flexDirection: "column", background: T.sidebar, borderRight: `1px solid ${T.border}` }}>

        {/* Logo — links to melvo.io, opens in new tab */}
        <div style={{ padding: "24px 24px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "center" }}>
          <a href="https://melvo.io" target="_blank" rel="noreferrer" style={{ display: "block", lineHeight: 0 }}>
            <img src="/melvo-logo.png" alt="Melvo" style={{ height: 100 }} />
          </a>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: "16px 10px", display: "flex", flexDirection: "column", gap: 2 }}>
          {NAV_ITEMS.map(item => (
            <button
              key={item}
              onClick={() => setPage(item)}
              className={`nav-btn ${page === item ? "active" : ""}`}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 10,
                padding: "9px 14px", borderRadius: 8, background: "none", border: "none",
                cursor: "pointer", textAlign: "left",
                color: page === item ? T.accent : T.dim,
                fontSize: 13, fontWeight: page === item ? 600 : 400,
                letterSpacing: "0.01em",
              }}>
              <span style={{ opacity: page === item ? 1 : 0.7 }}>{NAV_ICONS[item]}</span>
              {item}
            </button>
          ))}
        </nav>

        {/* Legal footer */}
        <div style={{ padding: "10px 14px", display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[
            { label: "Terms", href: "https://melvo.io/terms-of-service" },
            { label: "Privacy", href: "https://melvo.io/privacy-policy" },
            { label: "Messaging", href: "https://melvo.io/messaging-policy" },
          ].map(({ label, href }) => (
            <a key={label} href={href} target="_blank" rel="noreferrer"
              style={{ color: T.dim, fontSize: 10, fontWeight: 300, textDecoration: "none", letterSpacing: "0.03em" }}
              onMouseEnter={e => e.target.style.color = T.muted}
              onMouseLeave={e => e.target.style.color = T.dim}>
              {label}
            </a>
          ))}
        </div>

        {/* Account */}
        <div style={{ padding: "16px 14px", borderTop: `1px solid ${T.border}` }}>
          {/* Message usage */}
          <div style={{ marginBottom: 14, padding: "10px 12px", background: T.surface, borderRadius: 10, border: `1px solid ${T.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ color: T.dim, fontSize: 10, fontWeight: 400, letterSpacing: "0.06em", textTransform: "uppercase" }}>Messages</span>
              <span style={{ color: pct >= 80 ? arcColor : T.muted, fontSize: 10, fontWeight: 600 }}>
                {msgCap.used} / {msgCap.cap}{msgCap.addon > 0 ? ` + ${msgCap.addon} banked` : ""}
              </span>
            </div>
            <div style={{ height: 2, borderRadius: 2, background: T.border2 }}>
              <div style={{ height: "100%", borderRadius: 2, background: arcColor, width: `${pct}%`, transition: "width 0.5s ease" }} />
            </div>
            {msgCap.addon > 0 && (
              <div style={{ marginTop: 6, fontSize: 9, fontWeight: 500, color: T.accent, letterSpacing: "0.04em" }}>
                ✦ {msgCap.addon} banked — rolls over monthly
              </div>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: T.accentDim, border: `1px solid ${T.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: T.accent, flexShrink: 0 }}>
              {initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "#d0d0e8", fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {businessName || "My Business"}
              </div>
              <button
                onClick={() => { localStorage.removeItem("melvo_token"); localStorage.removeItem("melvo_business_id"); window.location.reload(); }}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: T.dim, fontSize: 11, fontWeight: 300 }}
                onMouseEnter={e => e.target.style.color = T.error}
                onMouseLeave={e => e.target.style.color = T.dim}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* ── Main ── */}
      <main style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Top bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 32px", height: 60, background: "rgba(6,6,8,0.9)", backdropFilter: "blur(16px)", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: T.dim, fontSize: 13, fontWeight: 300 }}>{businessName || "Melvo"}</span>
            <span style={{ color: T.border2, fontSize: 13 }}>›</span>
            <span style={{ color: T.text, fontSize: 13, fontWeight: 500 }}>{page}</span>
          </div>

          {/* Notification bell */}
          <button style={{ width: 36, height: 36, borderRadius: 9, background: T.surface, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", position: "relative" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.dim} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            <span style={{ position: "absolute", top: 7, right: 7, width: 6, height: 6, borderRadius: "50%", background: T.accent }} />
          </button>
        </div>

        {/* Page content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "32px 36px" }}>
          {pages[page]}
        </div>
      </main>
    </div>
  );
}
